// pages/VizLoaderOrderPage.tsx
import { useParams } from 'react-router-dom'
import { useSelector } from '../../../../Flux/index.js'
import type { AppDispatch } from '../../../../Flux/index.js'
import type { State } from '../../../../Flux/Slice/index.js'
import { Actions } from '../../../../Flux/Slice/index.js'
import VizWorkspace from './VizWorkspace.js'
import { canUseSavedViews, canUseVisualisationOnOrder } from '../../../../Model/index.js'
import { useGetAuthenticatedUser } from '../../../../Flux/Selector/index.js'
import { orderSlice } from '../../../../Flux/Slice/Order/OrderSlice.js'
import { ScopeNotFoundNotice } from '../../ScopeNotFoundNotice.js'
import { useWorkspaceHydration } from './UseWorkspaceHydration.js'
import { useScopeLoad } from './UseScopeLoad.js'

function requestOrder(dispatch: AppDispatch, orderId: string) {
  dispatch(Actions[orderSlice.name].getOrderRequest({ orderId }))
  dispatch(Actions[orderSlice.name].getAllDatasetRequest({ orderId }))
}

/**
 * Order-scoped entry point for the visualization workspace
 */
export default function VizLoaderOrderPage() {
  const { id } = useParams<{ id: string }>()

  const user = useGetAuthenticatedUser()

  // selectors
  const order = useSelector((s: typeof State) => {
    const o = s.order?.order
    return o && o !== 'LOADING' && o.id === id ? o : undefined
  })
  const orderNotFound = useSelector((s: typeof State) => !!id && s.order?.notFoundOrderId === id)

  // Must match the order menu's gate, which counts a lab-shared licence: otherwise its entry is a dead end.
  const visualisationAccess = canUseVisualisationOnOrder(order, user)
  const savedViewsAccess = canUseSavedViews(user)

  const loaded = useScopeLoad('order', id, requestOrder)
  const orderReady = loaded.scope
  const samplesReady = loaded.samples
  const allReady = orderReady && samplesReady

  const { hydrated, resumable } = useWorkspaceHydration(id, allReady, savedViewsAccess)

  // Deny only once the order is actually known. A lab-shared licence is a property of the order, so
  // evaluating this while it is still loading would flash "no access" at precisely the users who do
  // have it.
  if (user && order && !visualisationAccess) {
    return <div className="p-6">You do not have access to this feature.</div>
  }

  if (!id) return <div className="p-6">No order id in URL.</div>

  if (orderNotFound) {
    return (
      <div className="p-6">
        <ScopeNotFoundNotice scopeKind="order" />
      </div>
    )
  }

  // Once hydrated, a re-fetch of the scope's own data (a reconnect, an edit) must not unmount the workspace.
  if (!allReady && !hydrated) {
    return (
      <div className="p-8">
        <h1 className="text-3xl mb-4">Visualization</h1>
        <div className="flex items-center gap-3 text-sm">
          <span className="loading loading-spinner" />
          <span>
            Loading… <span className="opacity-70">{`order: ${orderReady ? '✓' : '…'} · datasets: ${samplesReady ? '✓' : '…'}`}</span>
          </span>
        </div>
      </div>
    )
  }

  if (!hydrated) return null

  return <VizWorkspace orderOrProjectId={id} resumable={resumable} />
}
